import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedActions from '../components/FixedActions';
import { Award, Users, Target, Shield, TrendingUp, Heart, CheckCircle, Star } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Award size={40} />,
      title: 'Excellence',
      description: 'We strive for perfection in every project, delivering world-class sports facilities and construction services with unmatched quality.'
    },
    {
      icon: <Users size={40} />,
      title: 'Team Work',
      description: 'Our experienced team collaborates seamlessly to ensure every project exceeds client expectations and industry standards.'
    },
    {
      icon: <Target size={40} />,
      title: 'Innovation',
      description: 'We embrace cutting-edge technology and modern construction techniques to deliver superior and sustainable results.'
    },
    {
      icon: <Shield size={40} />,
      title: 'Reliability',
      description: 'Timely project completion and consistent quality delivery have made us a trusted partner for over 15 years.'
    }
  ];

  const milestones = [
    { year: '2009', title: 'Founded', description: 'Right Shade began its journey in Bangalore' },
    { year: '2012', title: '100 Projects', description: 'Completed 100+ successful projects' },
    { year: '2016', title: 'Pan-India Expansion', description: 'Expanded operations across 15+ cities' },
    { year: '2020', title: 'Industry Leader', description: 'Recognized as top sports construction company' },
    { year: '2024', title: '500+ Projects', description: 'Milestone of 500+ completed projects' }
  ];

  const achievements = [
    { icon: <CheckCircle size={24} />, text: 'ISO 9001:2015 Certified' },
    { icon: <CheckCircle size={24} />, text: 'FIFA Approved Vendor' },
    { icon: <CheckCircle size={24} />, text: 'Green Building Certified' },
    { icon: <CheckCircle size={24} />, text: 'Award Winning Designs' }
  ];

  return (
    <div style={{ background: '#FAFBFC' }}>
      <Header />
      <FixedActions />
      
      {/* Hero Section */}
      <section style={{
        paddingTop: '140px',
        paddingBottom: '80px',
        background: 'linear-gradient(135deg, #1A1B1E 0%, #2D2E32 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23FFD700" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.4
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 700,
              color: 'white',
              marginBottom: '24px',
              fontFamily: "'Poppins', sans-serif"
            }}>
              Building Dreams, One Project at a Time
            </h1>
            <p style={{
              fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
              color: 'rgba(255,255,255,0.9)',
              lineHeight: 1.7,
              marginBottom: '16px',
              fontFamily: "'Inter', sans-serif"
            }}>
              With over 15 years of expertise in sports court construction and building development, Right Shade has established itself as a leading name in delivering premium quality projects across India.
            </p>
            <p style={{
              fontSize: 'clamp(1rem, 1.8vw, 1.25rem)',
              color: 'rgba(255,255,255,0.8)',
              lineHeight: 1.6,
              fontFamily: "'Inter', sans-serif"
            }}>
              Founded in 2009, we have successfully completed over 500 projects, ranging from professional sports facilities to modern residential and commercial constructions.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 700,
                marginBottom: '24px',
                color: '#1A1B1E',
                fontFamily: "'Poppins', sans-serif"
              }}>
                Our Story
              </h2>
              <p style={{
                fontSize: '18px',
                lineHeight: 1.8,
                color: '#4A5568',
                marginBottom: '20px',
                fontFamily: "'Inter', sans-serif"
              }}>
                Right Shade was founded in 2009 with a simple vision: to transform the sports infrastructure and construction landscape in India by delivering world-class facilities that inspire excellence.
              </p>
              <p style={{
                fontSize: '18px',
                lineHeight: 1.8,
                color: '#4A5568',
                marginBottom: '20px',
                fontFamily: "'Inter', sans-serif"
              }}>
                Over the years, we've grown from a small team with big dreams to one of India's most trusted names in sports court construction and building development. Our commitment to quality, innovation, and customer satisfaction has been the cornerstone of our success.
              </p>
              <p style={{
                fontSize: '18px',
                lineHeight: 1.8,
                color: '#4A5568',
                fontFamily: "'Inter', sans-serif"
              }}>
                Today, we continue to push boundaries, embrace new technologies, and deliver projects that exceed expectations. Every court we build, every structure we create, is a testament to our dedication to excellence.
              </p>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
              borderRadius: '24px',
              padding: '40px',
              color: 'white'
            }}>
              <h3 style={{
                fontSize: '28px',
                fontWeight: 600,
                marginBottom: '24px',
                fontFamily: "'Poppins', sans-serif"
              }}>
                Our Achievements
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {achievements.map((achievement, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {achievement.icon}
                    </div>
                    <span style={{ fontSize: '16px', fontWeight: 500 }}>{achievement.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section style={{ padding: '80px 0', background: 'white' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              marginBottom: '16px',
              color: '#1A1B1E',
              fontFamily: "'Poppins', sans-serif"
            }}>
              Our Core Values
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#718096',
              fontFamily: "'Inter', sans-serif"
            }}>
              The principles that guide our work and define our success
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {values.map((value, index) => (
              <div
                key={index}
                style={{
                  background: '#FAFBFC',
                  padding: '40px 32px',
                  borderRadius: '20px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  border: '2px solid transparent'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.borderColor = '#FFD700';
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(255,215,0,0.2)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.background = '#FAFBFC';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  margin: '0 auto 24px'
                }}>
                  {value.icon}
                </div>
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  marginBottom: '12px',
                  color: '#1A1B1E',
                  fontFamily: "'Poppins', sans-serif"
                }}>
                  {value.title}
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: '#4A5568',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section style={{ padding: '80px 0', background: '#1A1B1E', color: 'white' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              marginBottom: '16px',
              fontFamily: "'Poppins', sans-serif"
            }}>
              Our Journey
            </h2>
            <p style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.8)',
              fontFamily: "'Inter', sans-serif"
            }}>
              Key milestones that shaped Right Shade
            </p>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {milestones.map((milestone, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: '32px',
                  alignItems: 'flex-start',
                  position: 'relative',
                  paddingLeft: '60px'
                }}
              >
                <div style={{
                  position: 'absolute',
                  left: 0,
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '14px',
                  color: 'white',
                  flexShrink: 0
                }}>
                  {milestone.year}
                </div>
                <div style={{ flex: 1, paddingTop: '6px' }}>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    fontFamily: "'Poppins', sans-serif"
                  }}>
                    {milestone.title}
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    color: 'rgba(255,255,255,0.7)',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {milestone.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 0',
        background: 'linear-gradient(135deg, #FFD700, #FFA500)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            marginBottom: '20px',
            color: 'white',
            fontFamily: "'Poppins', sans-serif"
          }}>
            Ready to Start Your Project?
          </h2>
          <p style={{
            fontSize: '20px',
            color: 'rgba(255,255,255,0.95)',
            marginBottom: '32px',
            fontFamily: "'Inter', sans-serif"
          }}>
            Let's bring your vision to life with our expertise and dedication
          </p>
          <button
            className="btn"
            onClick={() => window.location.href = '/contact'}
            style={{
              padding: '16px 48px',
              fontSize: '18px',
              fontWeight: 600,
              background: '#1A1B1E',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'Inter', sans-serif"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.3)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Get In Touch
          </button>
        </div>
      </section>

      <style jsx>{`
        @media (max-width: 1024px) {
          section > div > div[style*="gridTemplateColumns"] {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
        
        @media (max-width: 768px) {
          section {
            padding: 60px 20px !important;
          }
          
          section:first-of-type {
            padding-top: 100px !important;
            padding-bottom: 60px !important;
          }
          
          /* Timeline adjustments */
          section:nth-of-type(2) > div > div {
            padding-left: 20px !important;
            padding-right: 0 !important;
          }
          
          /* Values cards */
          section:nth-of-type(4) > div > div:last-child {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          
          /* CTA section */
          section:last-of-type {
            padding: 60px 20px !important;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default About;
